// @ts-nocheck
import * as Color from 'color';
import { capitalize, compact } from 'lodash';
import moment from 'moment-timezone';
import { formatVariable } from 'sonata-react-components';
import { isCTVCampaign, isDigitalCampaign, isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import {
  normalize,
  normalizeWithUnit,
  normalizeWithUnitAndThousandAndMillions
} from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import {
  advancedFiltersVariables,
  statsKeys,
  statsPrefs
} from 'sonata-static-storage/lib/samples/statsPreferences.samples';
import { PricingModelEnum } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { indexOf, intersection, isEmpty } from 'taptap-tools/utils';

const NUM_OF_INDEXES_PER_ATTR = 10;
const MAX_INDEX_VALUE = 100;

const { daysDiff: DAYS_DIFFERENCES } = statsPrefs;

const CHARTS_DATE_FORMAT_TO_STEP = {
  long: {
    day: 'ddD',
    hour: 'HH',
    month: 'MMM YY',
    week: 'W-GGGG'
  },
  short: {
    day: 'D',
    hour: 'HH',
    month: 'MM YY',
    week: 'W GG'
  }
};

export const ANALYTICAL_VIEW_TIMEFRAME = {
  days: {
    format: 'YYYY-MM-DDTHH:mm:ss',
    subtraction: {
      quantity: 25,
      unit: 'hours'
    },
    timeFrame: 10,
    unit: 'days',
    unitOfTime: 'day'
  },
  hours: {
    format: 'YYYY-MM-DDTHH:mm:ss',
    subtraction: {
      quantity: 1,
      unit: 'hours'
    },
    timeFrame: 24,
    unit: 'hours',
    unitOfTime: 'hour'
  }
};

export const NUMBER_DAYS_MONTH = 31;
export const NUMBER_DAYS_WEEK = 7;
export const NUMBER_HOURS_DAY = 24;

const DOOH_DEMOGRAPHY_STATS_KEYS = {
  ages: ['12-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65+'],
  genders: ['M', 'F']
};

export function percentage(value, relative, ratio) {
  ratio = ratio || 100;
  const eng = value && relative ? (value * ratio) / relative : 0;
  if (eng !== 0) {
    return eng;
  }
  return 0;
}

export const getMetricsKeys = option => {
  switch (option) {
    case 'actionVideoComplete':
      return 'video_complete';
    case 'actionAudioComplete':
      return 'audio_complete';
    case 'visitDigitalConversion':
      return 'visit';
    case 'footfallAttribution':
      return 'footfall_attribution';
    case 'viewabilityInView':
      return 'view_inview';
    case 'mediaFee':
      return 'media_fee';
    case 'pagesViewed':
      return 'pages_viewed';
    case 'totalSpend':
      return 'total_spend';
    case 'visitRetentionRate':
      return 'visit_retention_rate';
    case 'storeVisitsRate':
      return 'footfall_attribution_rate';
    case 'storeVisitsCost':
      return 'cpfootfall_attribution';
    case 'viewabilityInViewRate':
      return 'view_inview_rate';
    default:
      return option;
  }
};

export function getDefaultMessageSelectorKpis(option) {
  switch (option) {
    case 'impressions':
      return 'Impressions';
    case 'clicks':
      return 'Clicks';
    case 'pageViewed':
      return 'Pages viewed';
    case 'actionVideoComplete':
      return 'Videos completed';
    case 'actionAudioComplete':
      return 'Audios completed';
    case 'visitDigitalConversion':
      return 'Web Visits';
    case 'footfallAttribution':
      return 'Store Visits';
    case 'viewabilityInView':
      return 'InView';
    case 'mediaFee':
      return 'Media Fee';
    case 'totalSpend':
      return 'Total invoice';
    case 'ctr':
      return 'CTR';
    case 'etr':
      return 'ETR';
    case 'hctr':
      return 'HCTR';
    case 'hvtr':
      return 'HVTR';
    case 'vtr':
      return 'VTR';
    case 'atr':
      return 'ATR';
    case 'pvtr':
      return 'PVTR';
    case 'retentionRate':
      return 'Retention Rate';
    case 'storeVisitsRate':
      return 'Store Visits Rate';
    case 'true_hctr':
      return 'True HCTR';
    case 'true_hvtr':
      return 'True HVTR';
    case 'viewInviewRate':
      return 'IVR';
    case 'attn_lumen_attentive_per_mille':
      return 'APM';
    case 'attn_lumen_avg_view_time':
      return 'Avg. View Time';
    case 'attn_lumen_viewed_rate':
      return 'Viewed %';
    default:
      return null;
  }
}

export function ctr(clicks, impressions) {
  return percentage(clicks, impressions, 100);
}

export function engagement(conversions, impressions) {
  return percentage(conversions, impressions, 100);
}

export function cpa(spend, actions) {
  return percentage(spend, actions, 1);
}

export function checkAdFormat(formats, ads, stats = {}) {
  const videoFormats = ['video', 'vast'];
  const audioFormats = ['daast'];
  let formatFound = false;
  ads.every(ad => {
    if (indexOf(formats, ad.adFormat) > -1) {
      formatFound = true;
    } else if (intersection(formats, videoFormats).length > 0) {
      const videoKpis = statsKeys.video;
      videoKpis.every(videoKpi => {
        if (stats[videoKpi]) {
          formatFound = true;
        }
        return !formatFound;
      });
    } else if (intersection(formats, audioFormats).length > 0) {
      const audioKpis = statsKeys.audio;
      audioKpis.every(audioKpi => {
        if (stats[audioKpi]) {
          formatFound = true;
        }
        return !formatFound;
      });
    }
    return !formatFound;
  });
  return formatFound;
}

export const REPORTING_COLORS = {
  appInstalls: '#ffd923',
  atr: '#c12680',
  bidMultiplier: '#f8b739',
  clicks: '#63C1FB',
  ctr: '#1fbaa4',
  dwellTime: '#ff4528',
  dwellTimeAcc: '#2172b8',
  ecpc: '#5d3191',
  ecpe: '#5d3191',
  ecppv: '#5d3191',
  etr: '#1fbaa4',
  footfallAttribution: '#0f8830',
  footfallAttributionRate: '#ff4528',
  hctr: '#1fbaa4',
  hvtr: '#18a39b',
  impressions: '#ED9D5B',
  impressionsKpisChart: '#676E8A',
  lumenApm: '#9b59b6',
  lumenAvgViewTime: '#3498db',
  lumenViewedRate: '#2ecc71',
  mediaFee: '#69e26e',
  playouts: '#f1b583',
  proximityVisits: '#A5C97A',
  pvtr: '#A5C97A',
  true_ecpc: '#5d3191',
  true_ecpv: '#5d3191',
  true_hctr: '#1fbaa4',
  true_hvtr: '#18a39b',
  viewInView: '#f1b583',
  viewInViewRate: '#f8b739',
  visitBounceRate: '#ff4528',
  visitRate: '#ff4528',
  vtr: '#18a39b'
};

export const HEATMAP_COLORS = ['#0000ff', '#82B805', '#FFFF00', '#ff0000'];

function addECPCChart(charts) {
  Object.assign(charts, {
    ecpc: {
      color: REPORTING_COLORS.ecpc,
      order: 5,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.ecpc,
            marker: {
              lineColor: REPORTING_COLORS.ecpc
            },
            tooltip: {
              className: 'currency'
            }
          },
          statKey: 'ecpc'
        }
      ]
    }
  });
}

function addTrueKPICharts(charts) {
  Object.assign(charts, {
    true_ecpc: {
      color: REPORTING_COLORS.true_ecpc,
      order: 6,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.true_ecpc,
            marker: {
              lineColor: REPORTING_COLORS.true_ecpc
            },
            tooltip: {
              className: 'currency'
            }
          },
          statKey: 'true_ecpc'
        }
      ]
    },
    true_ecpv: {
      color: REPORTING_COLORS.true_ecpv,
      order: 12,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.true_ecpv,
            marker: {
              lineColor: REPORTING_COLORS.true_ecpv
            },
            tooltip: {
              className: 'currency'
            }
          },
          statKey: 'true_ecpv'
        }
      ]
    },
    true_hctr: {
      color: REPORTING_COLORS.true_hctr,
      order: 4,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.true_hctr,
            marker: {
              lineColor: REPORTING_COLORS.true_hctr
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'true_hctr'
        }
      ]
    },
    true_hvtr: {
      color: REPORTING_COLORS.true_hvtr,
      order: 11,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.true_hvtr,
            marker: {
              lineColor: REPORTING_COLORS.true_hvtr
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'true_hvtr'
        }
      ]
    }
  });
}

function addEngagementsCharts(charts) {
  Object.assign(charts, {
    ecpe: {
      color: REPORTING_COLORS.ecpe,
      order: 6,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.ecpe,
            marker: {
              lineColor: REPORTING_COLORS.ecpe
            },
            tooltip: {
              className: 'currency'
            }
          },
          statKey: 'ecpe'
        }
      ]
    },
    etr: {
      color: REPORTING_COLORS.etr,
      order: 7,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.etr,
            marker: {
              lineColor: REPORTING_COLORS.etr
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'etr'
        }
      ]
    }
  });
}

function addPagesViewedCharts(charts) {
  Object.assign(charts, {
    ecppv: {
      color: REPORTING_COLORS.ecppv,
      order: 6,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.ecppv,
            marker: {
              lineColor: REPORTING_COLORS.ecppv
            },
            tooltip: {
              className: 'currency'
            }
          },
          statKey: 'ecppv'
        }
      ]
    },
    pvtr: {
      color: REPORTING_COLORS.pvtr,
      order: 7,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.etr,
            marker: {
              lineColor: REPORTING_COLORS.pvtr
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'pvtr'
        }
      ]
    }
  });
}

function addAppInstallsChart(charts, stats) {
  if (stats.click_install && stats.click_install > 0) {
    Object.assign(charts, {
      appsInstallsRate: {
        color: REPORTING_COLORS.appInstalls,
        order: 15,
        stats: [
          {
            seriesConfig: {
              color: REPORTING_COLORS.appInstalls,
              marker: {
                lineColor: REPORTING_COLORS.appInstalls
              },
              tooltip: {
                className: 'percent'
              }
            },
            statKey: 'app_install_rate'
          }
        ]
      }
    });
  }
}

function addMediaFeeChart(charts) {
  Object.assign(charts, {
    mediaFee: {
      color: REPORTING_COLORS.mediaFee,
      order: 14,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.mediaFee,
            marker: {
              lineColor: REPORTING_COLORS.mediaFee
            },
            tooltip: {
              className: 'currency'
            }
          },
          statKey: 'media_fee'
        }
      ]
    }
  });
}

function addViewabilityChart(charts) {
  charts.impressions.stats.push(
    {
      seriesConfig: {
        color: '#222',
        lineColor: '#ddd',
        lineWidth: 1,
        marker: {
          lineColor: '#ddd'
        }
      },
      statKey: 'view_measured'
    },
    {
      seriesConfig: {
        color: REPORTING_COLORS.viewInView,
        lineWidth: 1,
        marker: {
          lineColor: REPORTING_COLORS.viewInView
        }
      },
      statKey: 'view_inview'
    }
  );
  Object.assign(charts, {
    viewInviewRate: {
      color: REPORTING_COLORS.viewInViewRate,
      order: 9,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.viewInViewRate,
            marker: {
              lineColor: REPORTING_COLORS.viewInViewRate
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'view_inview_rate'
        }
      ]
    }
  });
}

function addLumenAttentionCharts(charts) {
  charts.impressions.stats.push(
    {
      seriesConfig: {
        color: '#222',
        lineColor: '#ddd',
        lineWidth: 1,
        marker: {
          lineColor: '#ddd'
        }
      },
      statKey: 'attn_lumen_measured'
    },
    {
      seriesConfig: {
        color: REPORTING_COLORS.lumenApm,
        lineWidth: 1,
        marker: {
          lineColor: REPORTING_COLORS.lumenApm
        }
      },
      statKey: 'attn_lumen_viewable'
    }
  );
  Object.assign(charts, {
    attnLumenApm: {
      color: REPORTING_COLORS.lumenApm,
      order: 10,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.lumenApm,
            marker: {
              lineColor: REPORTING_COLORS.lumenApm
            }
          },
          statKey: 'attn_lumen_attentive_per_mille'
        }
      ]
    },
    attnLumenAvgViewTime: {
      color: REPORTING_COLORS.lumenAvgViewTime,
      order: 11,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.lumenAvgViewTime,
            marker: {
              lineColor: REPORTING_COLORS.lumenAvgViewTime
            }
          },
          statKey: 'attn_lumen_avg_view_time'
        }
      ]
    },
    attnLumenViewedRate: {
      color: REPORTING_COLORS.lumenViewedRate,
      order: 12,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.lumenViewedRate,
            marker: {
              lineColor: REPORTING_COLORS.lumenViewedRate
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'attn_lumen_viewed_rate'
        }
      ]
    }
  });
}

function addFootfallAttributionChart(
  charts,
  reportingType,
  useBackups,
  user,
  userRoles,
  hasLegacyFootfallAttribution,
  isDriveToStore
) {
  if (!isDOOHCampaign(reportingType)) {
    charts.clicks.yAxis = [
      {
        labels: {
          style: {
            color: '#ccc',
            fontFamily: 'Verdana, sans-serif',
            fontSize: '10px'
          }
        },
        min: 0,
        opposite: true,
        tickWidth: 0,
        title: { text: null }
      }
    ];
    if (hasLegacyFootfallAttribution) {
      charts.clicks.stats.push({
        seriesConfig: {
          color: '#222',
          lineColor: '#ddd',
          lineWidth: 1,
          marker: {
            lineColor: '#ddd'
          },
          yAxis: 1
        },
        statKey: 'footfall_attribution'
      });
    }
    if (isDriveToStore) {
      charts.clicks.stats.push({
        seriesConfig: {
          color: '#222',
          lineColor: '#ddd',
          lineWidth: 1,
          marker: {
            lineColor: '#ddd'
          },
          yAxis: 1
        },
        statKey: 'fa_venue_uplift'
      });
    }
    if (checkUserResourcePermissions('reportingEcpc', user, userRoles) && !useBackups) {
      if (hasLegacyFootfallAttribution) {
        charts.ecpc.stats.push({
          seriesConfig: {
            color: '#222',
            lineColor: '#ddd',
            lineWidth: 1,
            marker: {
              lineColor: '#ddd'
            }
          },
          statKey: 'cpfootfall_attribution'
        });
      }
      if (isDriveToStore) {
        charts.ecpc.stats.push({
          seriesConfig: {
            color: '#222',
            lineColor: '#ddd',
            lineWidth: 1,
            marker: {
              lineColor: '#ddd'
            }
          },
          statKey: 'fa_venue_cost_per_visit'
        });
      }
    }
  }
  if (!useBackups) {
    if (hasLegacyFootfallAttribution) {
      charts.footfallAttributionRate = {
        color: REPORTING_COLORS.footfallAttributionRate,
        order: 12,
        stats: [
          {
            seriesConfig: {
              color: REPORTING_COLORS.footfallAttributionRate,
              marker: {
                lineColor: REPORTING_COLORS.footfallAttributionRate
              },
              tooltip: {
                className: 'percent'
              }
            },
            statKey: 'footfall_attribution_rate'
          }
        ]
      };
    }
    if (isDriveToStore) {
      charts.driveToStoreUpliftRate = {
        color: REPORTING_COLORS.footfallAttributionRate,
        order: 13,
        stats: [
          {
            seriesConfig: {
              color: REPORTING_COLORS.footfallAttributionRate,
              marker: {
                lineColor: REPORTING_COLORS.footfallAttributionRate
              },
              tooltip: {
                className: 'percent'
              }
            },
            statKey: 'fa_venue_uplift_pct'
          }
        ]
      };
    }
  }
}

function addStoreVisitChart(charts, hasBaseline, hasLegacyFootfallAttribution, isDriveToStore) {
  if (hasLegacyFootfallAttribution) {
    Object.assign(charts, {
      storeVisits: {
        color: REPORTING_COLORS.footfallAttribution,
        order: 11,
        stats: [
          {
            seriesConfig: {
              color: REPORTING_COLORS.footfallAttribution,
              marker: {
                lineColor: REPORTING_COLORS.footfallAttribution
              }
            },
            statKey: 'footfall_attribution'
          }
        ],
        yAxis: [
          {
            labels: {
              style: {
                color: '#ccc',
                fontFamily: 'Verdana, sans-serif',
                fontSize: '10px'
              }
            },
            min: 0,
            opposite: true,
            tickWidth: 0,
            title: { text: null }
          }
        ]
      }
    });
    if (hasBaseline) {
      charts.storeVisits.stats.push(
        {
          seriesConfig: {
            color: REPORTING_COLORS.proximityVisits,
            marker: {
              lineColor: REPORTING_COLORS.proximityVisits
            }
          },
          statKey: 'proximity_visits'
        },
        {
          seriesConfig: {
            color: '#222',
            lineColor: '#ddd',
            marker: {
              lineColor: '#ddd'
            },
            yAxis: 1
          },
          statKey: 'organic_traffic'
        }
      );
    }
  }
  if (isDriveToStore) {
    Object.assign(charts, {
      driveToStoreVisits: {
        color: REPORTING_COLORS.footfallAttribution,
        order: 11.5,
        stats: [
          {
            seriesConfig: {
              color: REPORTING_COLORS.footfallAttribution,
              marker: {
                lineColor: REPORTING_COLORS.footfallAttribution
              }
            },
            statKey: 'fa_venue_uplift'
          },
          {
            seriesConfig: {
              color: '#222',
              lineColor: '#ddd',
              marker: {
                lineColor: '#ddd'
              },
              yAxis: 1
            },
            statKey: 'fa_venue_baseline'
          }
        ],
        yAxis: [
          {
            labels: {
              style: {
                color: '#ccc',
                fontFamily: 'Verdana, sans-serif',
                fontSize: '10px'
              }
            },
            min: 0,
            opposite: true,
            tickWidth: 0,
            title: { text: null }
          }
        ]
      }
    });
  }
}

function addDigitalVisitsChart(charts, stats, user, userRoles) {
  if (stats.visit && stats.visit > 0) {
    charts.clicks.stats.push({
      seriesConfig: {
        color: '#222',
        lineColor: '#ddd',
        lineWidth: 1,
        marker: {
          lineColor: '#ddd'
        }
      },
      statKey: 'visit'
    });
    if (checkUserResourcePermissions('reportingEcpc', user, userRoles)) {
      charts.ecpc.stats.push({
        seriesConfig: {
          color: '#222',
          lineColor: '#ddd',
          lineWidth: 1,
          marker: {
            lineColor: '#ddd'
          }
        },
        statKey: 'cp_visit',
        tooltip: {
          className: 'currency'
        }
      });
    }
    charts.visitRate = {
      color: REPORTING_COLORS.visitRate,
      order: 9,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.visitRate,
            marker: {
              lineColor: REPORTING_COLORS.visitRate
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'visit_rate'
        }
      ]
    };
    charts.visitBounceRate = {
      color: REPORTING_COLORS.visitBounceRate,
      order: 10,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.visitBounceRate,
            marker: {
              lineColor: REPORTING_COLORS.visitBounceRate
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'visit_bounce_rate'
        },
        {
          seriesConfig: {
            color: '#ddd',
            marker: {
              lineColor: '#ddd'
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'visit_retention_rate'
        }
      ]
    };
  } else if (stats.section && stats.section > 0) {
    charts.clicks.stats.push({
      seriesConfig: {
        color: '#222',
        lineColor: '#ddd',
        lineWidth: 1,
        marker: {
          lineColor: '#ddd'
        }
      },
      statKey: 'section'
    });
    if (checkUserResourcePermissions('reportingEcpc', user, userRoles)) {
      charts.ecpc.stats.push({
        seriesConfig: {
          color: '#222',
          lineColor: '#ddd',
          lineWidth: 1,
          marker: {
            lineColor: '#ddd'
          }
        },
        statKey: 'cp_section',
        tooltip: {
          className: 'currency'
        }
      });
    }
  } else if (stats.final && stats.final > 0) {
    charts.clicks.stats.push({
      seriesConfig: {
        color: '#222',
        lineColor: '#ddd',
        lineWidth: 1,
        marker: {
          lineColor: '#ddd'
        }
      },
      statKey: 'final'
    });
    if (checkUserResourcePermissions('reportingEcpc', user, userRoles)) {
      charts.ecpc.stats.push({
        seriesConfig: {
          color: '#222',
          lineColor: '#ddd',
          lineWidth: 1,
          marker: {
            lineColor: '#ddd'
          }
        },
        statKey: 'cp_final',
        tooltip: {
          className: 'currency'
        }
      });
    }
  }
}

function addAudioChart(charts) {
  Object.assign(charts, {
    atr: {
      color: REPORTING_COLORS.atr,
      order: 8,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.atr,
            marker: {
              lineColor: REPORTING_COLORS.atr
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'atr'
        }
      ]
    }
  });
}

function addVideoChart(charts) {
  Object.assign(charts, {
    vtr: {
      color: REPORTING_COLORS.vtr,
      order: 5,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.vtr,
            marker: {
              lineColor: REPORTING_COLORS.vtr
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'vtr'
        }
      ]
    }
  });
}

function addHouseholdVideoChart(charts) {
  Object.assign(charts, {
    hvtr: {
      color: REPORTING_COLORS.hvtr,
      order: 7,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.hvtr,
            marker: {
              lineColor: REPORTING_COLORS.hvtr
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'hvtr'
        }
      ]
    }
  });
}

function addCtrChart(charts) {
  Object.assign(charts, {
    ctr: {
      color: REPORTING_COLORS.ctr,
      order: 3,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.ctr,
            marker: {
              lineColor: REPORTING_COLORS.ctr
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'ctr'
        }
      ]
    }
  });
}

function addHctrChart(charts) {
  Object.assign(charts, {
    hctr: {
      color: REPORTING_COLORS.hctr,
      order: 3,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.hctr,
            marker: {
              lineColor: REPORTING_COLORS.hctr
            },
            tooltip: {
              className: 'percent'
            }
          },
          statKey: 'hctr'
        }
      ]
    }
  });
}

function addDoohChart(charts) {
  charts.impressions.stats.push({
    seriesConfig: {
      color: REPORTING_COLORS.playouts,
      lineWidth: 1,
      marker: {
        lineColor: REPORTING_COLORS.playouts
      }
    },
    statKey: 'playouts'
  });
  Object.assign(charts, {
    bidMultiplier: {
      color: REPORTING_COLORS.bidMultiplier,
      order: 2,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.bidMultiplier,
            marker: {
              lineColor: REPORTING_COLORS.bidMultiplier
            },
            tooltip: {
              className: 'x'
            }
          },
          statKey: 'bidMultiplier'
        }
      ]
    }
  });
}

function addDwellTimeChart(charts) {
  Object.assign(charts, {
    dwellTimeChart: {
      color: REPORTING_COLORS.dwellTimeAcc,
      order: 13,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.dwellTimeAcc,
            pointWidth: 50,
            type: 'column'
          },
          statKey: 'dwellTimeAcc'
        },
        {
          seriesConfig: {
            color: REPORTING_COLORS.dwellTime,
            type: 'spline'
          },
          statKey: 'dwellTime'
        }
      ],
      yAxis: [
        {
          labels: {
            style: {
              color: '#ccc',
              fontFamily: 'Verdana, sans-serif',
              fontSize: '10px'
            }
          },
          min: 0,
          opposite: true,
          tickWidth: 0,
          title: { text: null }
        }
      ]
    }
  });
}

function addAgeAndGender(charts) {
  Object.assign(charts, {
    ageAndGender: {
      order: 3,
      stats: [
        {
          statKey: 'ageAndGender'
        }
      ]
    }
  });
}

function addImpressionsKpisChart(charts) {
  Object.assign(charts, {
    impressionsKpisChart: {
      color: REPORTING_COLORS.impressionsKpisChart,
      order: 0,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.impressionsKpisChart,
            marker: {
              lineColor: REPORTING_COLORS.impressionsKpisChart
            }
          },
          statKey: 'impressionsKpisChart'
        }
      ]
    }
  });
}

export function getDefaultReportingCharts(
  stats,
  reportingType,
  reportingKpisIncluded,
  useBackups,
  hasHouseHoldImpactsEnabled,
  user,
  userRoles,
  campaigns
) {
  const {
    hasAppInstalls,
    hasAudio,
    hasBaseline,
    hasDigitalVisits,
    hasFootfallAttribution,
    hasLumenAttention,
    hasVideo,
    hasViewability,
    isAgeAndGenderVisible
  } = reportingKpisIncluded;
  let charts = {
    impressions: {
      color: REPORTING_COLORS.impressions,
      order: 1,
      stats: [
        {
          seriesConfig: {
            color: REPORTING_COLORS.impressions,
            marker: {
              lineColor: REPORTING_COLORS.impressions
            }
          },
          statKey: 'impressions'
        }
      ]
    }
  };
  if (!isDOOHCampaign(reportingType)) {
    charts = {
      ...charts,
      clicks: {
        color: REPORTING_COLORS.clicks,
        order: 2,
        stats: [
          {
            seriesConfig: {
              color: REPORTING_COLORS.clicks,
              marker: {
                lineColor: REPORTING_COLORS.clicks
              }
            },
            statKey: 'clicks'
          }
        ]
      }
    };
  }
  if (reportingType === CampaignFamilyEnum.standard) {
    addCtrChart(charts);
  }
  if (isCTVCampaign(reportingType) && hasHouseHoldImpactsEnabled) {
    addHctrChart(charts);
  }
  if (!isDigitalCampaign(reportingType)) {
    addDoohChart(charts);
  }
  if (hasAppInstalls) {
    addAppInstallsChart(charts, stats);
  }
  if (isDigitalCampaign(reportingType) && checkUserResourcePermissions('reportingEcpc', user, userRoles)) {
    addECPCChart(charts);
  }
  if (
    isCTVCampaign(reportingType) &&
    hasHouseHoldImpactsEnabled &&
    checkUserResourcePermissions('reportingHouseholdImpacts', user, userRoles) &&
    checkUserResourcePermissions('reportingHouseholdTrueKPIs', user, userRoles)
  ) {
    addTrueKPICharts(charts);
  }
  if (
    havePricingModel(campaigns, PricingModelEnum.ppe) &&
    checkUserResourcePermissions('reportingEcpe', user, userRoles)
  ) {
    addEngagementsCharts(charts);
  }
  if (
    havePricingModel(campaigns, PricingModelEnum.pppv) &&
    checkUserResourcePermissions('reportingEcpe', user, userRoles)
  ) {
    addPagesViewedCharts(charts);
  }
  if (checkUserResourcePermissions('reportingMediaFee', user, userRoles)) {
    addMediaFeeChart(charts);
  }
  if (checkUserResourcePermissions('reportingKPIsGraphs', user, userRoles)) {
    addImpressionsKpisChart(charts);
  }
  if (hasViewability && checkUserResourcePermissions('reportingIasViewabilityEvents', user, userRoles)) {
    addViewabilityChart(charts);
  }
  if (hasLumenAttention && !isDOOHCampaign(reportingType)) {
    addLumenAttentionCharts(charts);
  }
  if (
    hasFootfallAttribution &&
    (checkUserResourcePermissions('reportingFootfallAttribution', user, userRoles) ||
      checkUserResourcePermissions('reportingTotalFootfallAttribution', user, userRoles))
  ) {
    const isDriveToStore = campaignsHaveDriveToStore(campaigns);
    const hasLegacyFootfallAttribution = campaigns.some(
      campaign => campaign.hasNewAttributionStats && !campaign.hasDriveToStore
    );
    addFootfallAttributionChart(
      charts,
      reportingType,
      useBackups,
      user,
      userRoles,
      hasLegacyFootfallAttribution,
      isDriveToStore
    );
    checkUserResourcePermissions('reportingFootfallAttributionTable', user, userRoles) &&
      addStoreVisitChart(charts, hasBaseline, hasLegacyFootfallAttribution, isDriveToStore);
    checkUserResourcePermissions('reportingDwellTime', user, userRoles) && !useBackups && addDwellTimeChart(charts);
  }
  if (hasDigitalVisits && checkUserResourcePermissions('reportingDigitalConversions', user, userRoles)) {
    addDigitalVisitsChart(charts, stats, user, userRoles);
  }
  if (hasAudio) {
    addAudioChart(charts);
  }
  if (hasVideo) {
    if (isCTVCampaign(reportingType) && hasHouseHoldImpactsEnabled) {
      addHouseholdVideoChart(charts);
    } else {
      addVideoChart(charts);
    }
  }
  if (isAgeAndGenderVisible) {
    addAgeAndGender(charts);
  }
  return charts;
}

export function formatTimeFrame(
  timeFrame,
  timezone,
  unitOfTime = 'day',
  datesFormat = 'YYYY-MM-DDTHH:mm:ss',
  hasCustomDates = false,
  openFAWindow = false
) {
  const beginDateOffset = moment(timeFrame.start).tz(timezone).format('Z');
  const endDateOffset = moment(timeFrame.end).tz(timezone).format('Z');
  const today = moment.utc().endOf(unitOfTime);
  let endDate = moment
    .utc(timeFrame.end)
    .endOf(unitOfTime)
    .add(openFAWindow && !hasCustomDates ? 15 : 0, 'days');
  if (endDate.isAfter(today)) {
    endDate = today;
  }
  return {
    end: endDate.format(datesFormat) + endDateOffset,
    start: moment.utc(timeFrame.start).startOf(unitOfTime).format(datesFormat) + beginDateOffset
  };
}

export function getInitialTimeFrame(selectedCampaigns) {
  if (selectedCampaigns.length) {
    let beginDate = null;
    let endDate = null;
    let timezone = null;
    let hasAttribution = false;
    selectedCampaigns.forEach(campaign => {
      hasAttribution = !!campaign.hasNewAttributionStats;
      if (!beginDate || beginDate > moment.utc(campaign.beginDate)) {
        beginDate = moment.utc(campaign.beginDate);
      }
      if (campaign.endDate) {
        if (!endDate || endDate < moment.utc(campaign.endDate)) {
          endDate = moment.utc(campaign.endDate);
        }
      }
      if (!timezone) {
        ({ timezone } = campaign);
      }
    });
    const today = moment().tz(timezone).set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0);
    endDate = moment
      .utc(endDate)
      .endOf('day')
      .add(hasAttribution ? 15 : 0, 'days');
    if (!endDate || (endDate && today.diff(endDate, 'days') < 0)) {
      endDate = today;
    }
    return {
      beginDate: beginDate.startOf('day').format('YYYY-MM-DD'),
      endDate: endDate.startOf('day').format('YYYY-MM-DD')
    };
  }
  return {
    beginDate: null,
    endDate: null
  };
}

function getTodayStart() {
  return moment().format('YYYY-MM-DD');
}

function getTodayEnd() {
  return moment().format('YYYY-MM-DD');
}

export function getYesterdayFormatted() {
  return moment().subtract(1, 'days').format('YYYY-MM-DD');
}

function getAWeekAgo() {
  return moment().subtract(7, 'days').format('YYYY-MM-DD');
}

function getThisMonthStart() {
  return moment().date(1).format('YYYY-MM-DD');
}

function getThisMonthEnd() {
  return moment().add(1, 'months').date(0).format('YYYY-MM-DD');
}

function getAMonthAgo() {
  return moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
}

function getLastMonthEnd() {
  return moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD');
}

export function getNewTimeFrame(timeRange, initialTimeFrame) {
  const START_DATE_METHODS = {
    last7Days: getAWeekAgo,
    lastMonth: getAMonthAgo,
    thisMonth: getThisMonthStart,
    today: getTodayStart,
    yesterday: getYesterdayFormatted
  };
  const END_DATE_METHODS = {
    last7Days: getTodayEnd,
    lastMonth: getLastMonthEnd,
    thisMonth: getThisMonthEnd,
    today: getTodayEnd,
    yesterday: getYesterdayFormatted
  };
  if (timeRange === 'fromStart') {
    return initialTimeFrame;
  }
  return {
    beginDate: START_DATE_METHODS[timeRange](),
    endDate: END_DATE_METHODS[timeRange](),
    hasCustomDates: true
  };
}

export function getTimeStepForTimeFrame(timeFrame) {
  const startDate = moment(timeFrame.beginDate);
  const endDate = moment(timeFrame.endDate);
  const difference = endDate.diff(startDate, 'days');
  if (difference < DAYS_DIFFERENCES.hour) {
    return 'hour';
  }
  if (difference < DAYS_DIFFERENCES.day) {
    return 'day';
  }
  if (difference < DAYS_DIFFERENCES.week) {
    return 'week';
  }
  return 'month';
}

export function campaignsHaveViewability(campaigns = [], aggregatedStats = {}) {
  return (
    campaigns.some(campaign => campaign.hasViewability) &&
    statsPrefs.viewabilityKeys.some(kpi => aggregatedStats?.[kpi] > 0)
  );
}

export function campaignsHaveLumenAttention(aggregatedStats = {}) {
  return statsPrefs.lumenKeys.some(kpi => aggregatedStats?.[kpi] > 0);
}

export function campaignsHaveCTVEvents(campaigns = [], aggregatedStats = {}) {
  return (
    campaigns.some(({ type }) => isCTVCampaign(type)) && statsPrefs.ctvKeys.some(kpi => aggregatedStats?.[kpi] > 0)
  );
}

export function campaignsHaveVideo(campaigns = [], campaignsStats = {}) {
  const ads = campaigns.reduce((acc, campaign) => acc.concat(campaign.ads), []);
  return checkAdFormat(['vast', 'video'], ads, campaignsStats);
}

export function campaignsHaveAudio(campaigns = [], campaignsStats = {}) {
  const ads = campaigns.reduce((acc, campaign) => acc.concat(campaign.ads), []);
  return checkAdFormat(['daast'], ads, campaignsStats);
}

export function campaignsHaveFootfallAttribution(campaigns = []) {
  return campaigns.some(campaign => campaign.hasNewAttributionStats);
}

export function campaignsHaveBaseline(campaigns = []) {
  return campaigns.some(campaign => campaign.hasBaseline);
}

export function campaignsHaveDriveToStore(campaigns = []) {
  return campaigns.some(campaign => campaign.hasDriveToStore);
}

export function campaignsHaveLegacyFootfallAttribution(campaigns = []) {
  return campaigns.some(campaign => campaign.hasNewAttributionStats && !campaign.hasDriveToStore);
}

export function campaignsHaveAppInstall(campaignsStats = {}) {
  const statsKeysCatalog = Object.keys(campaignsStats || {});
  return intersection(statsKeysCatalog, statsKeys.appInstallKeys).length > 0;
}

export function campaignsHaveDigitalConversions(campaigns) {
  return campaigns.some(c => c.userPixels.length > 0);
}

export function getDateFormatted(chartTimeStep, date, shortVersion = false) {
  return moment
    .utc(date)
    .format(CHARTS_DATE_FORMAT_TO_STEP[shortVersion ? 'short' : 'long'][chartTimeStep] || chartTimeStep);
}

export function copyToClipboard(text) {
  const ta = document.createElement('textarea');
  ta.innerText = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  ta.remove();
}

export const adsFormatValues = [
  {
    label: 'Display',
    values: ['banner', 'video']
  },
  {
    label: 'Audio',
    values: ['daast']
  },
  {
    label: 'Video',
    values: ['vast', 'rewardedVast']
  },
  {
    label: 'Native',
    values: ['native']
  }
];

export function formatAdsFormat(adsFormat) {
  return adsFormat.reduce((acc, actualItem) => {
    adsFormatValues.forEach(adFormat => {
      if (adFormat.values.includes(actualItem) && !acc.includes(adFormat.label)) {
        acc.push(adFormat.label);
      }
    });
    return acc;
  }, []);
}

export function getUniquesAdsFormat(ads) {
  return ads.reduce((acc, actualItem) => {
    if (acc.includes(actualItem.adFormat)) {
      return acc;
    }
    return [...acc, actualItem.adFormat];
  }, []);
}

export const MetricsKpis = {
  kpis: 'kpis',
  metrics: 'metrics',
  unknown: 'unknown'
};

export function getMetricsKpis(id) {
  const metrics = [
    'media_fee',
    'ecpc',
    'ecpv',
    'total_spend',
    'cp_footfall_attribution',
    'cpfootfall_attribution',
    'cp_visit',
    'cp_section',
    'cp_final',
    'fa_venue_cost_per_visit',
    'fa_venue_total_spent',
    'fa_venue_income'
  ];
  const kpis = [
    'vtr',
    'ctr',
    'atr',
    'view_inview_rate',
    'footfall_attribution_rate',
    'avg_footfall_attribution_rate',
    'visit_bounce_rate',
    'visit_retention_rate',
    'visit_rate',
    'section_rate',
    'final_rate',
    'fa_venue_uplift_pct',
    'fa_venue_roi'
  ];
  if (metrics.includes(id)) {
    return MetricsKpis.metrics;
  }
  if (kpis.includes(id)) {
    return MetricsKpis.kpis;
  }
  return MetricsKpis.unknown;
}

export function formatNumber(id, value, currencyInfo, forceDot = false) {
  switch (getMetricsKpis(id)) {
    case MetricsKpis.metrics:
      return normalizeWithUnitAndThousandAndMillions(value, currencyInfo);
    case MetricsKpis.kpis:
      return `${normalize(value, 2, currencyInfo.countries[0])}%`;
    default:
      return formatVariable(value, 2, currencyInfo.countries[0], false, forceDot);
  }
}

export function formatValue(value, id, currency, fixed, rawMode) {
  const metric = getMetricsKpis(id);
  if (rawMode && metric === MetricsKpis.metrics) {
    return normalizeWithUnit(value, currency, 2);
  }
  if (rawMode && metric === MetricsKpis.kpis) {
    return `${normalize(value, 2, currency.countries[0])}%`;
  }
  return normalize(value, fixed, currency.countries[0]);
}

export function getUniquesAdsFormatMultiCampaign(campaigns) {
  const adsFormats = campaigns.map(campaign => getUniquesAdsFormat(campaign.ads));
  return adsFormats.reduce((acc, actualItem) => {
    actualItem.forEach(adFormat => {
      if (acc.includes(adFormat)) {
        return acc;
      }
      acc.push(adFormat);
    });
    return acc;
  }, []);
}

export function formatDetailedStats(stats, timeFrame, timeStep) {
  const iterations = moment(timeFrame.end).diff(moment(timeFrame.start), `${timeStep}s`);
  const formattedStats = [];
  for (let i = 0; i < iterations; i++) {
    const iterationDate = moment.utc(timeFrame.end).subtract(i, `${timeStep}s`).format('YYYY-MM-DDT00:00:00.000');
    const entry = stats.find(statsEntry => statsEntry.sdate === `${iterationDate}Z`);
    let formattedStatsEntry = {
      date: iterationDate
    };
    if (entry) {
      const { sdate, ...rest } = entry;
      formattedStatsEntry = {
        ...formattedStatsEntry,
        ...rest
      };
    }
    formattedStats.push(formattedStatsEntry);
  }
  return formattedStats;
}

function getOverallGoal(reportingCampaigns, goalType) {
  return reportingCampaigns.reduce((acc, campaign) => {
    return acc + (campaign.overallGoal && campaign.overallGoal.type === goalType ? campaign.overallGoal.target : 0);
  }, 0);
}

const getIcon = name => {
  const icons = [
    {
      class: 'fak fa-impressions',
      name: 'face'
    },
    {
      class: 'fak fa-viewability',
      name: 'rule'
    },
    {
      class: 'fa-regular fa-hand-back-point-up',
      name: 'mouse'
    },
    {
      class: 'fal fa-coins',
      name: 'coins'
    },
    {
      class: 'fal fa-video',
      name: 'camera'
    },
    {
      class: 'fal fa-glasses-alt',
      name: 'glasses'
    },
    {
      class: 'fal fa-store-alt',
      name: 'shop'
    },
    {
      class: 'fal fa-headphones-alt',
      name: 'headphones'
    },
    {
      class: 'fal fa-tablet-alt',
      name: 'tablet'
    },
    {
      class: 'fal fa-tasks',
      name: 'form'
    },
    {
      class: 'fal fa-venus-mars',
      name: 'gender'
    },
    {
      class: 'fal fa-chart-line',
      name: 'chart'
    },
    {
      class: 'fal fa-globe-pointer',
      name: 'website'
    },
    {
      class: 'fal fa-handshake',
      name: 'engagements'
    },
    {
      class: 'fa-light fa-arrows-to-eye',
      name: 'viewed'
    }
  ];

  const icon = icons.find(i => i.name === name) || icons[0];
  return icon.class;
};

function getAnalyticalViewMetricData(reportingAnalyticalView, metric, formatFunction?) {
  return {
    analyticalView:
      reportingAnalyticalView?.lastDays.map(item =>
        formatFunction ? formatFunction(item[metric] || 0) : item[metric] || 0
      ) || [],
    analyticalViewHours:
      reportingAnalyticalView?.lastHours.map(item =>
        formatFunction ? formatFunction(item[metric] || 0) : item[metric] || 0
      ) || []
  };
}

export function getImpressionsItem(
  reportingAnalyticalView,
  reportingCampaigns,
  reportingType,
  stats,
  countriesIncludeHouseHoldImpactsEvents,
  someCountryIncludesHouseHoldImpactsEvents,
  user,
  userRoles
) {
  const impressionsItem = {
    familyItemApplies: true,
    goalMax: getOverallGoal(reportingCampaigns, 'impressions') || undefined,
    main: {
      ...getAnalyticalViewMetricData(reportingAnalyticalView, 'impressions'),
      icon: getIcon('face'),
      metricKey: 'impressions',
      name: 'reporting.metrics.impressions',
      value: stats.impressions
    },
    progressBarColor: REPORTING_COLORS.impressions,
    secondary: []
  };

  const householdImpactsPermission = checkUserResourcePermissions('reportingHouseholdImpacts', user, userRoles);
  if (householdImpactsPermission && isCTVCampaign(reportingType) && countriesIncludeHouseHoldImpactsEvents) {
    impressionsItem.secondary.push({
      metricKey: 'households_reached',
      name: 'reporting.metrics.households_reached',
      value: stats.households_reached || 0
    });
    impressionsItem.secondary.push({
      metricKey: 'total_viewers',
      name: 'reporting.metrics.total_viewers',
      value: stats.total_viewers || 0
    });
  }

  const uniqueUsersPermission = checkUserResourcePermissions('reportingUniqueUsers', user, userRoles);
  if (
    uniqueUsersPermission &&
    (reportingType === CampaignFamilyEnum.standard ||
      (isCTVCampaign(reportingType) && !someCountryIncludesHouseHoldImpactsEvents))
  ) {
    impressionsItem.secondary.push({
      metricKey: 'unique_users',
      name: 'reporting.metrics.unique',
      value: stats.unique || stats.unique_users || 0
    });
  }

  if (!isDigitalCampaign(reportingType) && stats.playouts) {
    impressionsItem.secondary.push({
      metricKey: 'playouts',
      name: 'reporting.metrics.playouts',
      value: stats.playouts || 0
    });
  }
  return impressionsItem;
}

export function getClicksItem(
  reportingAnalyticalView,
  reportingCampaigns,
  stats,
  countriesIncludeHouseHoldImpactsEvents,
  user,
  userRoles
) {
  const reportingHouseholdTrueKPIsPermission = checkUserResourcePermissions(
    'reportingHouseholdTrueKPIs',
    user,
    userRoles
  );
  const reportingHouseholdImpactsPermission = checkUserResourcePermissions(
    'reportingHouseholdImpacts',
    user,
    userRoles
  );
  const clicksItem = {
    familyItemApplies: true,
    goalMax: getOverallGoal(reportingCampaigns, 'clicks') || undefined,
    main: {},
    progressBarColor: REPORTING_COLORS.clicks,
    secondary: []
  };
  const clickObject = {
    metricKey: 'clicks',
    name: 'reporting.metrics.clicks',
    ...getAnalyticalViewMetricData(reportingAnalyticalView, 'clicks')
  };
  const shouldShowCTVMetrics =
    countriesIncludeHouseHoldImpactsEvents &&
    reportingCampaigns.some(reportingCampaign => isCTVCampaign(reportingCampaign.type));
  const ctrObject = {
    decimalDigits: 2,
    isPercentage: true,
    ...(reportingHouseholdImpactsPermission &&
      reportingHouseholdTrueKPIsPermission &&
      shouldShowCTVMetrics && {
        metric2Key: 'true_hctr',
        name2: 'reporting.metrics.true_hctr',
        value2: stats.true_hctr
      }),
    metricKey: shouldShowCTVMetrics ? 'hctr' : 'ctr',
    name: shouldShowCTVMetrics ? 'reporting.metrics.hctr' : 'reporting.metrics.ctr'
  };
  let ecpcObject;
  clickObject.value = stats.clicks;
  ctrObject.value = ctr(stats.clicks, stats.impressions);
  const cpcPermission = checkUserResourcePermissions('reportingEcpc', user, userRoles);
  if (cpcPermission) {
    ecpcObject = {
      decimalDigits: 2,
      isCurrency: true,
      ...(shouldShowCTVMetrics &&
        reportingHouseholdImpactsPermission &&
        reportingHouseholdTrueKPIsPermission && {
          metric2Key: 'true_ecpc',
          name2: 'reporting.metrics.true_ecpc',
          value2: stats.true_ecpc
        }),
      metricKey: 'ecpc',
      name: 'reporting.metrics.ecpc',
      value: stats.ecpc
    };
  }
  clicksItem.main = {
    ...clickObject,
    icon: getIcon('mouse')
  };
  clicksItem.secondary.push(...compact([ctrObject, ecpcObject]));
  return clicksItem;
}

export function getPagesViewedItem(reportingAnalyticalView, reportingCampaigns, stats, user, userRoles) {
  const clicksItem = {
    familyItemApplies: true,
    goalMax: getOverallGoal(reportingCampaigns, 'pages_viewed') || undefined,
    main: {},
    progressBarColor: REPORTING_COLORS.clicks,
    secondary: []
  };
  const pageViewedObject = {
    metricKey: 'pages_viewed',
    name: 'reporting.metrics.pagesViewed',
    ...getAnalyticalViewMetricData(reportingAnalyticalView, 'pages_viewed')
  };
  const pvtrObject = {
    decimalDigits: 2,
    isPercentage: true,
    metricKey: 'pvtr',
    name: 'reporting.metrics.pvtr'
  };
  let ecppvObject;
  pageViewedObject.value = stats.pages_viewed;
  pvtrObject.value = stats.pvtr;
  const cpcPermission = checkUserResourcePermissions('reportingEcppv', user, userRoles);
  if (cpcPermission) {
    ecppvObject = {
      decimalDigits: 2,
      isCurrency: true,
      metricKey: 'ecppv',
      name: 'reporting.metrics.ecppv',
      value: stats.ecppv
    };
  }
  clicksItem.main = {
    ...pageViewedObject,
    icon: getIcon('website')
  };
  clicksItem.secondary.push(...compact([pvtrObject, ecppvObject]));
  return clicksItem;
}

export function getEngagementsItem(reportingAnalyticalView, reportingCampaigns, stats, user, userRoles) {
  const clicksItem = {
    familyItemApplies: true,
    goalMax: getOverallGoal(reportingCampaigns, 'clicks') || undefined,
    main: {},
    progressBarColor: REPORTING_COLORS.clicks,
    secondary: []
  };
  const clickObject = {
    metricKey: 'engagements',
    name: 'reporting.metrics.engagements',
    ...getAnalyticalViewMetricData(reportingAnalyticalView, 'engagements')
  };
  const etrObject = {
    decimalDigits: 2,
    isPercentage: true,
    metricKey: 'etr',
    name: 'reporting.metrics.etr'
  };
  let ecpeObject;
  clickObject.value = stats.engage;
  etrObject.value = engagement(stats.engage, stats.impressions);
  const cpePermission = checkUserResourcePermissions('reportingEcpe', user, userRoles);
  if (cpePermission) {
    ecpeObject = {
      decimalDigits: 2,
      isCurrency: true,
      metricKey: 'ecpe',
      name: 'reporting.metrics.ecpe',
      value: stats.ecpe
    };
  }
  clicksItem.main = {
    ...clickObject,
    icon: getIcon('engagements')
  };
  clicksItem.secondary.push(...compact([etrObject, ecpeObject]));
  return clicksItem;
}

export function getVideoItem(
  reportingAnalyticalView,
  reportingCampaigns,
  stats,
  countriesIncludeHouseHoldImpactsEvents,
  user,
  userRoles
) {
  const reportingHouseholdTrueKPIsPermission = checkUserResourcePermissions(
    'reportingHouseholdTrueKPIs',
    user,
    userRoles
  );
  const reportingHouseholdImpactsPermission = checkUserResourcePermissions(
    'reportingHouseholdImpacts',
    user,
    userRoles
  );
  let videoItem = {
    familyItemApplies:
      reportingCampaigns.some(reportingCampaign => !isDOOHCampaign(reportingCampaign.type)) &&
      campaignsHaveVideo(reportingCampaigns, stats),
    goalMax: getOverallGoal(reportingCampaigns, 'video_complete') || undefined,
    main: {
      analyticalView: reportingAnalyticalView?.lastDays.map(item => item.video_complete || 0) || [],
      analyticalViewHours: reportingAnalyticalView?.lastHours.map(item => item.video_complete || 0) || [],
      icon: getIcon('camera'),
      metricKey: 'video_complete',
      name: 'reporting.metrics.actionVideoComplete',
      value: stats.video_complete || 0
    },
    progressBarColor: REPORTING_COLORS.vtr
  };
  const vtrPermission = checkUserResourcePermissions('reportingVtr', user, userRoles);
  const shouldShowCTVMetrics =
    countriesIncludeHouseHoldImpactsEvents &&
    reportingCampaigns.some(reportingCampaign => isCTVCampaign(reportingCampaign.type));
  if (vtrPermission) {
    videoItem = {
      ...videoItem,
      secondary: [
        {
          decimalDigits: 2,
          isPercentage: true,
          metricKey: shouldShowCTVMetrics ? 'hvtr' : 'vtr',
          ...(reportingHouseholdImpactsPermission &&
            reportingHouseholdTrueKPIsPermission &&
            shouldShowCTVMetrics && {
              metric2Key: 'true_vtr',
              name2: 'reporting.metrics.true_hvtr',
              value2: stats.true_hvtr
            }),
          name: shouldShowCTVMetrics ? 'reporting.metrics.hvtr' : 'reporting.metrics.vtr',
          value: stats.vtr || 0
        }
      ]
    };
  }
  const ecpvPermission = checkUserResourcePermissions('reportingEcpv', user, userRoles);
  if (ecpvPermission) {
    !videoItem.secondary ? (videoItem.secondary = []) : undefined;
    videoItem.secondary.push({
      decimalDigits: 2,
      isCurrency: true,
      metricKey: 'ecpv',
      ...(shouldShowCTVMetrics &&
        reportingHouseholdImpactsPermission &&
        reportingHouseholdTrueKPIsPermission && {
          metric2Key: 'true_ecpv',
          name2: 'reporting.metrics.true_ecpv',
          value2: stats.true_ecpv
        }),
      name: 'reporting.metrics.ecpv',
      value: stats.ecpv || 0
    });
  }
  return videoItem;
}

export function getAudioItem(reportingAnalyticalView, reportingCampaigns, stats, user, userRoles) {
  let audioItem = {
    familyItemApplies:
      reportingCampaigns.some(reportingCampaign => !isDOOHCampaign(reportingCampaign.type)) &&
      campaignsHaveAudio(reportingCampaigns, stats),
    goalMax: getOverallGoal(reportingCampaigns, 'audio_complete') || undefined,
    main: {
      ...getAnalyticalViewMetricData(reportingAnalyticalView, 'audio_complete'),
      icon: getIcon('headphones'),
      metricKey: 'audio_complete',
      name: 'reporting.metrics.actionAudioComplete',
      value: stats.audio_complete || 0
    },
    progressBarColor: REPORTING_COLORS.atr,
    secondary: []
  };
  const atrPermission = checkUserResourcePermissions('reportingAtr', user, userRoles);
  if (atrPermission) {
    audioItem.secondary.push({
      decimalDigits: 2,
      isPercentage: true,
      metricKey: 'atr',
      name: 'reporting.metrics.atr',
      value: stats.atr || 0
    });
  }
  const ecpaPermission = checkUserResourcePermissions('reportingEcpa', user, userRoles);
  if (ecpaPermission) {
    !audioItem.secondary ? (audioItem.secondary = []) : undefined;
    audioItem.secondary.push({
      decimalDigits: 2,
      isCurrency: true,
      metricKey: 'ecpa',
      name: 'reporting.metrics.ecpa',
      value: stats.ecpa || 0
    });
  }
  return audioItem;
}

export function getAttributionItem(reportingAnalyticalView, reportingCampaigns, showGTotal, stats, user, userRoles) {
  let attributionCpvValue = 0;
  if (stats.footfall_attribution) {
    const costDV = showGTotal ? stats.total_spend : stats.media_fee;
    attributionCpvValue = costDV / (stats.footfall_attribution || 0);
  }
  const statsMainValue = stats.footfall_attribution || 0;
  const statsMainCopyId = 'reporting.metrics.venue';
  const statsSecondaryValue = stats.footfall_attribution_rate || 0;
  const metricKey = 'footfall_attribution';
  const { analyticalView, analyticalViewHours } = getAnalyticalViewMetricData(
    reportingAnalyticalView,
    'footfall_attribution'
  );
  const secondaryMetricKey = 'footfall_attribution_rate';
  const tertiaryMetricKey = 'cpfootfall_attribution';
  const visitsRatePermission = checkUserResourcePermissions('reportingFootfallAttributionRate', user, userRoles);
  const visitsCostPermission = checkUserResourcePermissions('reportingCPFootfallAttribution', user, userRoles);

  const attributionItem = {
    familyItemApplies: campaignsHaveLegacyFootfallAttribution(reportingCampaigns),
    main: {
      analyticalView,
      analyticalViewHours,
      decimalDigits: 2,
      icon: getIcon('glasses'),
      metricKey,
      name: statsMainCopyId,
      value: statsMainValue
    },
    secondary: []
  };

  if (visitsRatePermission) {
    attributionItem.secondary.push({
      decimalDigits: 2,
      isPercentage: true,
      metricKey: secondaryMetricKey,
      name: 'reporting.metrics.footfall_attribution_rate',
      value: statsSecondaryValue
    });
  }

  if (visitsCostPermission) {
    attributionItem.secondary.push({
      decimalDigits: 2,
      isCurrency: true,
      metricKey: tertiaryMetricKey,
      name: 'reporting.metrics.footfall_attribution_cost',
      value: attributionCpvValue
    });
  }

  return attributionItem;
}

export function getDriveToStoreAttributionItem(reportingAnalyticalView, reportingCampaigns, stats, user, userRoles) {
  const statsMainValue = stats.fa_venue_uplift || 0;
  const statsSecondaryValue = stats.fa_venue_uplift_pct || 0;
  const attributionCpvValue = stats.fa_venue_cost_per_visit || 0;
  const { analyticalView, analyticalViewHours } = getAnalyticalViewMetricData(
    reportingAnalyticalView,
    'fa_venue_uplift'
  );
  const visitsRatePermission = checkUserResourcePermissions('reportingFootfallAttributionRate', user, userRoles);
  const visitsCostPermission = checkUserResourcePermissions('reportingCPFootfallAttribution', user, userRoles);

  const driveToStoreAttributionItem = {
    familyItemApplies: campaignsHaveDriveToStore(reportingCampaigns),
    main: {
      analyticalView,
      analyticalViewHours,
      decimalDigits: 2,
      icon: getIcon('glasses'),
      metricKey: 'fa_venue_uplift',
      name: 'reporting.metrics.fa_venue_uplift',
      value: statsMainValue
    },
    secondary: []
  };

  if (visitsRatePermission) {
    driveToStoreAttributionItem.secondary.push({
      decimalDigits: 2,
      isPercentage: true,
      metricKey: 'fa_venue_uplift_pct',
      name: 'reporting.metrics.fa_venue_uplift_pct',
      value: statsSecondaryValue
    });
  }

  if (visitsCostPermission) {
    driveToStoreAttributionItem.secondary.push({
      decimalDigits: 2,
      isCurrency: true,
      metricKey: 'fa_venue_cost_per_visit',
      name: 'reporting.metrics.fa_venue_cost_per_visit',
      value: attributionCpvValue
    });
  }

  return driveToStoreAttributionItem;
}

export function getAppsInstallItem(reportingCampaigns, stats) {
  return {
    familyItemApplies: campaignsHaveAppInstall(reportingCampaigns),
    main: {
      icon: getIcon('tablet'),
      metricKey: 'app_install',
      name: 'reporting.metrics.appsInstalls',
      value: stats.app_install || 0
    },
    progressBarColor: REPORTING_COLORS.appInstalls,
    secondary: [
      {
        decimalDigits: 2,
        isPercentage: true,
        metricKey: 'app_install_rate',
        name: 'reporting.metrics.appInstallsRate',
        value: stats.app_install_rate || 0
      },
      {
        decimalDigits: 2,
        isCurrency: true,
        metricKey: 'cpapp_install',
        name: 'reporting.metrics.appInstallsCost',
        value: stats.cpapp_install || 0
      }
    ]
  };
}

export function getViewabilityItem(reportingAnalyticalView, reportingCampaigns, viewabilityStats) {
  return {
    familyItemApplies: campaignsHaveViewability(reportingCampaigns),
    main: {
      ...getAnalyticalViewMetricData(reportingAnalyticalView, 'view_measured'),
      icon: getIcon('rule'),
      metricKey: 'view_measured',
      name: 'reporting.metrics.viewabilityMeasured',
      value: viewabilityStats.view_measured || 0
    },
    progressBarColor: REPORTING_COLORS.viewInView,
    secondary: [
      {
        metricKey: 'view_inview',
        name: 'reporting.metrics.viewabilityInView',
        value: viewabilityStats.view_inview || 0
      },
      {
        decimalDigits: 2,
        isPercentage: true,
        metricKey: 'view_inview_rate',
        name: 'reporting.metrics.viewabilityInViewRate',
        value: viewabilityStats.view_inview_rate || 0
      }
    ]
  };
}

export function getLumenAttentionItem(reportingAnalyticalView, stats) {
  const formatFunction = value => value * 100;
  return {
    familyItemApplies: campaignsHaveLumenAttention(stats),
    footerConfig: {
      height: 24,
      image: '/assets/images/providers/powered_by_lumen.png',
      position: 'right'
    },
    main: {
      ...getAnalyticalViewMetricData(reportingAnalyticalView, 'attn_lumen_viewed_rate', formatFunction),
      icon: getIcon('viewed'),
      isPercentage: true,
      metricKey: 'attn_lumen_viewed_rate',
      name: 'reporting.summary.labels.lumenTitle',
      subtitle: 'reporting.summary.labels.lumenSubtitle',
      subtitleValue: stats.attn_lumen_measured || 0,
      value: formatFunction(stats.attn_lumen_viewed_rate || 0)
    },
    progressBarColor: REPORTING_COLORS.lumenApm,
    secondary: [
      {
        decimalDigits: 0,
        metricKey: 'attn_lumen_avg_view_time',
        name: 'reporting.metrics.attnLumenAvgViewTime',
        unit: 's',
        value: stats.attn_lumen_avg_view_time || 0
      },
      {
        decimalDigits: 2,
        isCurrency: true,
        metricKey: 'attn_lumen_acpm',
        name: 'reporting.metrics.attnLumenAcpm',
        value: stats.attn_lumen_acpm || 0
      },
      {
        decimalDigits: 0,
        metricKey: 'attn_lumen_attentive_per_mille',
        name: 'reporting.metrics.attnLumenApm',
        value: stats.attn_lumen_attentive_per_mille || 0
      }
    ]
  };
}

export function getDigitalConversionItem(reportingAnalyticalView, reportingCampaigns, stats, user, userRoles) {
  const digitalConversionPermission = checkUserResourcePermissions('reportingCPDigitalConversion', user, userRoles);
  const digitalConversionsItem = {
    familyItemApplies: campaignsHaveDigitalConversions(reportingCampaigns),
    main: {
      ...getAnalyticalViewMetricData(reportingAnalyticalView, 'visit'),
      icon: getIcon('chart'),
      metricKey: 'visit',
      name: 'reporting.metrics.visitDigitalConversion',
      value: 0
    },
    progressBarColor: REPORTING_COLORS.visitRate,
    secondary: []
  };
  if (digitalConversionPermission) {
    digitalConversionsItem.secondary.push({
      decimalDigits: 2,
      isCurrency: true,
      metricKey: 'cpVisit',
      name: 'reporting.metrics.cpVisitDigitalConversion',
      value: stats.cpVisit || 0
    });
  }
  ['visit', 'section', 'final'].every(kpi => {
    if ((kpi === 'visit' && stats[kpi] === 0) || stats[kpi] > 0) {
      digitalConversionsItem.main = {
        ...getAnalyticalViewMetricData(reportingAnalyticalView, kpi),
        icon: getIcon('chart'),
        metricKey: kpi,
        name: `reporting.metrics.${kpi}DigitalConversion`,
        value: stats[kpi] || 0
      };
      if (digitalConversionPermission) {
        digitalConversionsItem.secondary = [
          {
            decimalDigits: 2,
            isCurrency: true,
            metricKey: `cp${kpi}`,
            name: `reporting.metrics.cp${capitalize(kpi)}DigitalConversion`,
            value: stats[`cp${kpi}`] || 0
          }
        ];
      }
    }
    return !(stats[kpi] > 0);
  });
  return digitalConversionsItem;
}

export function getCreativeFeeItem(totalCreativeFee) {
  if (totalCreativeFee === 0) {
    return;
  }
  return {
    decimalDigits: 2,
    isCurrency: true,
    name: 'reporting.metrics.creativeFee',
    value: totalCreativeFee
  };
}

export function getSpendItem(reportingAnalyticalView, showGTotal, totalCreativeFee, stats, user, userRoles) {
  const spendItem = {
    familyItemApplies: true,
    main: {},
    secondary: []
  };
  const spendObject = {
    ...getAnalyticalViewMetricData(reportingAnalyticalView, 'media_fee'),
    decimalDigits: 2,
    isCurrency: true,
    metricKey: 'media_fee',
    name: 'reporting.metrics.mediaFee',
    value: stats.media_fee
  };
  let gmObject;
  if (checkUserResourcePermissions('reportingGm', user, userRoles)) {
    gmObject = {
      decimalDigits: 2,
      isPercentage: true,
      metricKey: showGTotal ? 'total_gross_margin' : 'gross_margin',
      name: 'reporting.metrics.grossMargin',
      value: showGTotal ? stats.total_gross_margin : stats.gross_margin
    };
  }
  const gTotalObject = {
    ...getAnalyticalViewMetricData(reportingAnalyticalView, 'total_spend'),
    decimalDigits: 2,
    isCurrency: true,
    metricKey: 'total_spend',
    name: 'reporting.metrics.totalSpend',
    value: stats.total_spend
  };
  const creativeFeeItem = getCreativeFeeItem(totalCreativeFee);
  if (showGTotal) {
    const mediaFeePermission = checkUserResourcePermissions('reportingMediaFee', user, userRoles);
    spendItem.main = {
      ...gTotalObject,
      icon: getIcon('coins')
    };
    spendItem.secondary.push(...compact([creativeFeeItem || gmObject, mediaFeePermission ? spendObject : null]));
  } else {
    spendItem.main = {
      ...spendObject,
      icon: getIcon('coins')
    };
    gmObject && spendItem.secondary.push(gmObject);
  }
  return spendItem;
}

export function getDoohPatrimonyItem(reportingCampaigns, doohStats) {
  return {
    dooh: true,
    familyItemApplies: reportingCampaigns.some(reportingCampaign => isDOOHCampaign(reportingCampaign.type)),
    loading: isEmpty(doohStats),
    main: {
      icon: getIcon('chart'),
      metricKey: 'screensNumber',
      name: 'reporting.metrics.screens',
      value: doohStats ? doohStats.screensNumber : 0
    },
    secondary: [
      {
        metricKey: 'screenAvgImpacts',
        name: 'reporting.metrics.avgImpact',
        value: doohStats ? doohStats.screenAvgImpacts : 0
      }
    ]
  };
}

export function getDoohDemographyAgesItem(stats) {
  const { ages, genders } = DOOH_DEMOGRAPHY_STATS_KEYS;
  return {
    data: genders.map(gender => ages.map(age => stats[`${gender}${age}`])),
    dooh: true,
    graph: true,
    series: ages
  };
}

export function getCampaignCreativeFee(reportingCampaigns) {
  let totalCreativeFee = 0;
  reportingCampaigns.forEach(selectedCampaign => {
    totalCreativeFee += selectedCampaign.creativeFee;
  });
  return totalCreativeFee;
}

export function getItemKeyItemData(
  reportingAnalyticalView,
  reportingCampaigns,
  reportingType,
  totalCreativeFee,
  reportingStats,
  customMetricsOrKpis,
  user,
  userRoles,
  itemKey
) {
  const { countriesIncludeHouseHoldImpactsEvents, showGTotal, someCountryIncludesHouseHoldImpactsEvents } =
    customMetricsOrKpis;
  switch (itemKey) {
    case 'clicks':
      return getClicksItem(
        reportingAnalyticalView,
        reportingCampaigns,
        reportingStats,
        countriesIncludeHouseHoldImpactsEvents,
        user,
        userRoles
      );
    case 'pageViewed':
      return getPagesViewedItem(reportingAnalyticalView, reportingCampaigns, reportingStats, user, userRoles);
    case 'engagements':
      return getEngagementsItem(reportingAnalyticalView, reportingCampaigns, reportingStats, user, userRoles);
    case 'doohPatrimony':
      return getDoohPatrimonyItem(reportingCampaigns, reportingStats);
    case 'video':
      return getVideoItem(
        reportingAnalyticalView,
        reportingCampaigns,
        reportingStats,
        countriesIncludeHouseHoldImpactsEvents,
        user,
        userRoles
      );
    case 'audio':
      return getAudioItem(reportingAnalyticalView, reportingCampaigns, reportingStats, user, userRoles);
    case 'viewability':
      return getViewabilityItem(reportingAnalyticalView, reportingCampaigns, reportingStats);
    case 'lumenAttention':
      return getLumenAttentionItem(reportingAnalyticalView, reportingStats);
    case 'attribution':
      return getAttributionItem(
        reportingAnalyticalView,
        reportingCampaigns,
        showGTotal,
        reportingStats,
        user,
        userRoles
      );
    case 'driveToStoreAttribution':
      return getDriveToStoreAttributionItem(
        reportingAnalyticalView,
        reportingCampaigns,
        reportingStats,
        user,
        userRoles
      );
    case 'appsInstall':
      return getAppsInstallItem(reportingCampaigns, reportingStats);
    case 'digitalConversions':
      return getDigitalConversionItem(reportingAnalyticalView, reportingCampaigns, reportingStats, user, userRoles);
    case 'spend':
      return getSpendItem(reportingAnalyticalView, showGTotal, totalCreativeFee, reportingStats, user, userRoles);
    default:
      return getImpressionsItem(
        reportingAnalyticalView,
        reportingCampaigns,
        reportingType,
        reportingStats,
        countriesIncludeHouseHoldImpactsEvents,
        someCountryIncludesHouseHoldImpactsEvents,
        user,
        userRoles
      );
  }
}

export function getCampaignStatus(campaignDetailStats) {
  let campaignStatus = 'noData';
  if (campaignDetailStats) {
    const campaignGlobalStatus = campaignDetailStats.progress.find(campaignStatusEntry => {
      return campaignStatusEntry.timeFrame === 'overall' && campaignStatusEntry.type === 'budget';
    });
    campaignStatus = campaignGlobalStatus ? campaignGlobalStatus.progressGoal.status : campaignStatus;
  }
  return campaignStatus;
}

export function variableNameFormatter(variable) {
  return variable
    .split('.')
    .map(word => `${word.substring(0, 1).toUpperCase()}${word.substring(1)}`)
    .join('');
}

export function getVariableFilterPermission(variable) {
  return `reportingFilter${variableNameFormatter(variable)}`;
}

export function getLayerColors(baseColorSelected, minSelected = 0, maxSelected = 100) {
  const baseColor = Color(baseColorSelected[0]).darken(0.7);
  let currentColor;
  let weightRest = MAX_INDEX_VALUE;
  const rest = MAX_INDEX_VALUE / NUM_OF_INDEXES_PER_ATTR;
  const weightsList = [];
  while (weightRest > 0) {
    weightsList.push(weightRest);
    weightRest -= rest;
  }
  weightsList.reverse();
  return weightsList
    .map((weight, key) => {
      let disabledColor;
      if (baseColorSelected.length > 1) {
        currentColor = baseColorSelected[key]
          ? Color(baseColorSelected[key])
          : baseColorSelected[baseColorSelected.length - 1];
      } else {
        currentColor = key === 0 ? baseColor : currentColor.lighten(0.2);
      }
      if (weight < minSelected || weight > maxSelected) {
        disabledColor = '#403e3b';
      }
      return {
        color: disabledColor || currentColor.hex(),
        weight
      };
    })
    .sort((numA, numB) => numA - numB);
}

export function getMetricFormatFromOverviewItem(metricItem) {
  if (metricItem.isCurrency) {
    return 'currency';
  }
  if (metricItem.isPercentage) {
    return 'percentage';
  }
  return metricItem.decimalDigits ? 'float' : 'integer';
}

export function getVariableFilter(variable, campaigns) {
  if (variable.indexOf('device') > -1 && !campaigns.find(c => isDigitalCampaign(c.type))) {
    return false;
  }
  if (['lgid', 'lid'].includes(variable) && !campaigns.find(c => isDigitalCampaign(c.type))) {
    return false;
  }
  if (['screenId', 'screenGroupId'].includes(variable) && !campaigns.find(c => isDOOHCampaign(c.type))) {
    return false;
  }
  if (variable === 'dayOfWeekToggleId' && !campaigns.find(c => c.temporalPacing.dayOfWeekEnabled)) {
    return false;
  }
  if (variable === 'dayOfYearToggleId' && !campaigns.find(c => c.temporalPacing.dayOfYearEnabled)) {
    return false;
  }
  return true;
}

function range(start, stop) {
  return Array.from({ length: stop - start + 1 }, (_, i) => start + i);
}

export function getUserAgeRangeParsed(value) {
  const values = value.split('-');
  switch (value) {
    case '18-':
      return range(0, 17);
    case '65+':
      return range(65, 100);
    default:
      return range(parseInt(values[0]), parseInt(values[1]));
  }
}

export function getVariableTypeParsed(variableKey) {
  switch (variableKey) {
    case 'userAge':
      return 'userAgeRanges';
    default:
      return variableKey;
  }
}

export function getVariableFilterOriginalOptions(variableKey, variableValues) {
  if (variableKey !== 'userAge') {
    return variableValues;
  }
  const variableOptions = {
    17: '18-',
    24: '18-24',
    34: '25-34',
    44: '35-44',
    54: '45-54',
    64: '55-64',
    65: '65+'
  };
  return variableValues.reduce((acc, variable) => {
    return variableOptions[variable] ? [...acc, variableOptions[variable]] : acc;
  }, []);
}

export function getVariableFilterParsed(variableType, variableValues) {
  if (variableType === 'userAgeRanges') {
    return {
      userAge: variableValues.map(v => getUserAgeRangeParsed(v)).flat()
    };
  }
  return {
    [variableType]: variableValues
  };
}

export function isToday(date) {
  return moment.utc(date).isSame(moment.utc(), 'day');
}

export const STORE_VISITS = [
  'avgStoreVisits',
  'avg_footfall_attribution',
  'avg_store_visits',
  'cpavg_Footfall_attribution',
  'cpfootfall_attribution',
  'fa_venue_baseline',
  'fa_venue_cost_per_visit',
  'fa_venue_income',
  'fa_venue_roi',
  'fa_venue_total_spent',
  'fa_venue_uplift',
  'fa_venue_uplift_pct',
  'footfallAttribution',
  'footfall_attribution',
  'storeVisits',
  'storeVisitsCost',
  'storeVisitsRate'
];

export function getVariableFilters(user, userRoles, reportingCampaigns) {
  const atLeastOneDOOHCampaign = reportingCampaigns.some(({ type }) => isDOOHCampaign(type));
  const campaignsHaveAudience = reportingCampaigns.some(({ audienceLists }) => !!audienceLists.length);
  const campaignsHaveMORE = reportingCampaigns.some(({ optimizationModel }) => !!optimizationModel);
  const filtersKey = atLeastOneDOOHCampaign ? CampaignFamilyEnum.dooh : CampaignFamilyEnum.standard;
  return advancedFiltersVariables[filtersKey]
    .filter(variable => {
      return checkUserResourcePermissions(getVariableFilterPermission(variable), user, userRoles);
    })
    .filter(variable => {
      if (variable.includes('deterministic')) {
        return campaignsHaveAudience;
      }
      if (variable.includes('mloModelUsed')) {
        return campaignsHaveMORE;
      }
      return true;
    });
}

export function getCartographyFilter(generalCartographyFilters = {}, sectionCartographyFilters = {}) {
  const {
    cartographyCountries: generalCartographyCountries,
    cartographyLevel: generalCartographyLevel,
    cartographyLevelIds: generalCartographyLevelIds
  } = generalCartographyFilters;
  const {
    cartographyCountries: sectionCartographyCountries,
    cartographyLevel: sectionCartographyLevel,
    cartographyLevelIds: sectionCartographyLevelIds
  } = sectionCartographyFilters;

  const sectionHasCartography =
    !!sectionCartographyCountries?.length || (sectionCartographyLevel && !!sectionCartographyLevelIds.length);
  const generalHasCartography =
    !!generalCartographyCountries?.length || (generalCartographyLevel && !!generalCartographyLevelIds.length);

  if (sectionHasCartography) {
    if (sectionCartographyLevel && !!sectionCartographyLevelIds.length) {
      return { [sectionCartographyLevel]: sectionCartographyLevelIds };
    }
    return { level0: sectionCartographyCountries };
  }
  if (generalHasCartography) {
    if (generalCartographyLevel && !!generalCartographyLevelIds.length) {
      return { [generalCartographyLevel]: generalCartographyLevelIds };
    }
    return { level0: generalCartographyCountries };
  }
}

export function getTimeFrameForCustomTime(reportingTimeFrame, customTime, timeZone) {
  const { end, start } = reportingTimeFrame;
  const { format, subtraction, timeFrame, unit, unitOfTime } = customTime;
  let customStart = moment(start).tz(timeZone).startOf('day');
  const today = moment().tz(timeZone).endOf('hour');
  if (!isEmpty(subtraction)) {
    const { quantity, unit: subtractUnit } = subtraction;
    today.subtract(quantity, subtractUnit);
  }
  let customEnd = moment(end).tz(timeZone).endOf('day');
  if (customEnd.isAfter(today)) {
    customEnd = today;
  }
  const differenceInUnits = customEnd.diff(customStart, unitOfTime);
  if (differenceInUnits > timeFrame) {
    customStart = moment(customStart).subtract(timeFrame, unit);
  }
  const startDateOffset = customStart.format('Z');
  const endDateOffset = customEnd.format('Z');
  return {
    end: customEnd.format(format) + endDateOffset,
    start: customStart.format(format) + startDateOffset
  };
}

export function campaignsHaveContextualBidding(campaigns) {
  return campaigns.some(campaign => campaign.contextualBidding?.scenarios?.length > 0);
}

export function campaignsHaveDCO(campaigns) {
  return campaigns.some(campaign => campaign.dco?.scenarios?.length > 0);
}

export function getPermissions(user, userRoles, stats) {
  return {
    appsInstallsPermission: checkUserResourcePermissions('downloadAction', user, userRoles),
    avgFootfallAttributionPermission: checkUserResourcePermissions('reportingFootfallAttribution', user, userRoles),
    digitalConversionPermission: checkUserResourcePermissions('reportingDigitalConversions', user, userRoles),
    doohPermission: checkUserResourcePermissions('reportingDoohOverview', user, userRoles),
    footfallAttributionPermission: checkUserResourcePermissions('reportingTotalFootfallAttribution', user, userRoles),
    hasDoohDemographyStats: stats && !!(stats.M || stats.F),
    householdImpactsPermission: checkUserResourcePermissions('reportingHouseholdImpacts', user, userRoles),
    mediaFeePermission: checkUserResourcePermissions('reportingMediaFee', user, userRoles),
    reportingGTotal: checkUserResourcePermissions('reportingGTotal', user, userRoles),
    reportingHouseholdTrueKPIsPermission: checkUserResourcePermissions('reportingHouseholdTrueKPIs', user, userRoles),
    viewabilityPermission: checkUserResourcePermissions('reportingIasViewabilityEvents', user, userRoles)
  };
}

export function getReportingType(campaigns) {
  if (campaigns.every(campaign => isCTVCampaign(campaign.type))) {
    return CampaignFamilyEnum.ctv;
  }

  return campaigns.some(campaign => isDigitalCampaign(campaign.type))
    ? campaigns.some(campaign => isDOOHCampaign(campaign.type))
      ? 'both'
      : CampaignFamilyEnum.standard
    : CampaignFamilyEnum.dooh;
}

export function hasVideo(stats, campaigns) {
  const reportingType = getReportingType(campaigns);
  return !isDOOHCampaign(reportingType) && campaignsHaveVideo(campaigns, stats);
}

export function havePricingModel(campaigns, bidModelParam) {
  return campaigns.some(campaign => campaign.bidModels.some(bidModel => bidModel.bid.pricingModel === bidModelParam));
}

export function hasAudio(stats, campaigns) {
  const reportingType = getReportingType(campaigns);
  return !isDOOHCampaign(reportingType) && campaignsHaveAudio(campaigns, stats);
}
